<template>
  <VMultiModal :showing="showing" data-intercom-target="create-3d-modal">
    <EditorDeckModalsThreeDSlideCreator
      v-show="showing && step === STEPS.CHOOSE_CREATION_METHOD"
      :key="STEPS.CHOOSE_CREATION_METHOD"
      :showing="showing"
      :company-id="deck.company_id"
      data-testid="model-creator"
      @copy-from-another-deck="step = STEPS.CHOOSE_DECK"
      @compose-new-slide="step = STEPS.CHOOSE_MODEL"
      @close="$emit('update:showing', false)"
    />

    <EditorDeckModalsDeckSelector
      v-show="showing && step === STEPS.CHOOSE_DECK"
      :key="showing.toString()"
      :deck-id="deck.id"
      :showing="showing"
      :company-id="deck.company_id"
      :is-exporting="false"
      data-testid="deck-selector"
      @select-deck="selectDeck"
      @export="selectDeck"
      @close="$emit('update:showing', false)"
    >
      <template #header>
        <VModalHeader>
          <template #title>Choose a 3D Presentation</template>
          <template #sub-title>Create 3D Slide</template>
        </VModalHeader>
      </template>
      <template #footer>
        <div class="flex justify-end gap-2">
          <VButton variant="light" @click="step = STEPS.CHOOSE_CREATION_METHOD">Back</VButton>
        </div>
      </template>
    </EditorDeckModalsDeckSelector>

    <EditorDeckModalsDeckSceneSlideSelector
      v-if="showing && step === STEPS.CHOOSE_SCENE && state.selectedDeck"
      :key="`${state.selectedDeck.id}-${STEPS.CHOOSE_SCENE}`"
      :showing="showing"
      :selected-deck="state.selectedDeck"
      data-testid="model-selector"
      @copy-slides="copySlides"
      @close="$emit('update:showing', false)"
      @back="step = STEPS.CHOOSE_DECK"
    />

    <EditorDeckModalsModelSelector
      v-if="showing && step === STEPS.CHOOSE_MODEL"
      :key="STEPS.CHOOSE_MODEL"
      :showing="showing"
      :company-id="deck.company_id"
      data-testid="model-selector"
      @select-model="createSlide"
      @close="$emit('update:showing', false)"
    >
      <template #footer>
        <div class="flex justify-end gap-2">
          <VButton @click="step = STEPS.CHOOSE_CREATION_METHOD">Back</VButton>
        </div>
      </template>
    </EditorDeckModalsModelSelector>

    <VModal
      v-if="showing && step === STEPS.CAMERA_CREATE_LOADING"
      :key="STEPS.CAMERA_CREATE_LOADING"
      showing
      hide-close
      container-classes="min-w-128"
    >
      <template #header>
        <h2 class="text-lg font-medium text-gray-900 mb-0">Composing Scene</h2>
      </template>

      <div class="flex flex-col justify-center items-center py-8">
        <LoadingSpinner />
        <p class="mt-4">Composing Scene</p>
      </div>
    </VModal>
  </VMultiModal>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import { reactive, ref, watch } from 'vue'

import type { Model3D } from '@/entities/model3d'
import type { Deck, Slide } from '@/entities/deck'

enum STEPS {
  CHOOSE_CREATION_METHOD,
  CHOOSE_DECK,
  CHOOSE_SCENE,
  CHOOSE_MODEL,
  CAMERA_CREATE_LOADING,
}

const props = defineProps({
  deck: {
    type: Object as PropType<DeepReadonly<EditDeck>>,
    required: true,
  },
  showing: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['add-slide', 'error', 'copy-slides', 'update:showing'])

const step = ref<STEPS>(STEPS.CHOOSE_CREATION_METHOD)
const state = reactive({
  selectedDeck: null as Deck | null,
})

watch(
  () => props.showing,
  async (showing: boolean) => {
    if (!showing) {
      step.value = STEPS.CHOOSE_CREATION_METHOD
      return
    }
  },
)

const createSlide = async (model: Model3D) => {
  emit('add-slide', {
    deck_id: props.deck.id,
    type: 'M',
    model_id: model.id,
  })

  step.value = STEPS.CAMERA_CREATE_LOADING
}

function copySlides(slides: Slide[]) {
  emit(
    'copy-slides',
    slides.map(slide => ({ ...slide, deck_id: props.deck.id })),
  )
}

function selectDeck(deck: Deck) {
  step.value = STEPS.CHOOSE_SCENE
  state.selectedDeck = deck
}
</script>
